import { zodResolver } from '@hookform/resolvers/zod'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material'
import { IError } from 'api/axios'
import { useAuthContext } from 'components/context/AuthProvider'
import FormInput from 'components/form-elements/FormInput'
import NotificationSys, { KEEP_UNTIL_DISMISSED } from 'components/NotificationSystem'
import { EMAIL_IS_INVALID } from 'const'
import signLogo from 'images/sigin_logo.png'
import { useCallback, useRef } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { object, string, TypeOf } from 'zod'
import {
  ACCOUNT_INACTIVE,
  FAILED_SIGN_IN_ATTEMPTS_BEFORE_HINT,
  INCORRECT_CREDENTIALS,
  LICENSE_EXPIRED,
  LoginRefusalReason,
  SUBSCRIPTION_EXPIRED,
  SUPPORT_EMAIL,
} from '../../models/authMessages'

const supportLine = (
  <>
    For further assistance send a message to{' '}
    <MuiLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</MuiLink>.
  </>
)

const formSchema = object({
  email: string().min(1, 'Email is required').email(EMAIL_IS_INVALID),
  password: string().min(1, 'Password is required'),
})

type FormType = TypeOf<typeof formSchema>

export function AuthPageSignIn() {
  const { login, authLoading } = useAuthContext()

  const methods = useForm<FormType>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const {
    formState: { isDirty, isValid },
    handleSubmit,
  } = methods

  const navigate = useNavigate()

  const onClickForgot = useCallback(() => {
    navigate(routes.forgotPassword)
  }, [navigate])

  /**
   * Failed attempts in a row for one email. Lives in the tab only: signing in unmounts this page,
   * and a reload starts over.
   */
  const attemptsRef = useRef({ email: '', count: 0 })

  const countFailedAttempt = useCallback((email: string) => {
    const normalizedEmail = email.trim().toLowerCase()
    const previous = attemptsRef.current
    // Typing another email starts the count again — the hint is about one account, not the form.
    const count = previous.email === normalizedEmail ? previous.count + 1 : 1
    attemptsRef.current = { email: normalizedEmail, count }
    return count
  }, [])

  const showRefusal = useCallback((reason?: LoginRefusalReason) => {
    if (reason === 'license_expired') {
      NotificationSys.showWarning(
        <>
          {LICENSE_EXPIRED} {supportLine}
        </>,
        KEEP_UNTIL_DISMISSED,
      )
      return
    }

    if (reason === 'subscription_expired') {
      NotificationSys.showWarning(
        <>
          {SUBSCRIPTION_EXPIRED}{' '}
          <MuiLink component={Link} to={routes.addSubscription} sx={{ font: 'inherit' }}>
            payment
          </MuiLink>
          . {supportLine}
        </>,
        KEEP_UNTIL_DISMISSED,
      )
      return
    }

    NotificationSys.showWarning(
      <>
        {ACCOUNT_INACTIVE} Please contact Support at{' '}
        <MuiLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</MuiLink> for assistance with
        restoring access.
      </>,
      KEEP_UNTIL_DISMISSED,
    )
  }, [])

  const onSubmitHandler: SubmitHandler<FormType> = useCallback(
    (values: FormType) => {
      const { email, password } = values

      // Each attempt reports its own result, so whatever the previous one left on screen goes.
      NotificationSys.clearNotifications()

      login(email, password, undefined, (reason: IError) => {
        // A refused account says why; Forgot Password would not help, so no attempt counting.
        if (reason.response?.status === 403) {
          showRefusal(reason.response.data?.reason as LoginRefusalReason | undefined)
          return
        }

        // Only wrong credentials are counted.
        if (reason.response?.status !== 401) return

        if (countFailedAttempt(email) < FAILED_SIGN_IN_ATTEMPTS_BEFORE_HINT) {
          NotificationSys.showWarning(INCORRECT_CREDENTIALS, KEEP_UNTIL_DISMISSED)
          return
        }

        NotificationSys.showWarning(
          <>
            {INCORRECT_CREDENTIALS}
            <br />
            <br />
            If you forgot your password, use{' '}
            <MuiLink
              component="button"
              type="button"
              onClick={onClickForgot}
              sx={{ font: 'inherit', verticalAlign: 'baseline' }}
            >
              Forgot Password
            </MuiLink>{' '}
            to reset it. If you still cannot access your account, please contact support at{' '}
            <MuiLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</MuiLink>.
          </>,
          KEEP_UNTIL_DISMISSED,
        )
      })
    },
    [countFailedAttempt, login, onClickForgot, showRefusal],
  )

  return (
    <FormProvider {...methods}>
      <Box display="flex" justifyContent="center" mb={4}>
        <img src={signLogo} alt="Sign In Logo" />
      </Box>

      <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} width="100%" noValidate>
        <FormInput
          label="Email Address"
          autoComplete="email"
          name="email"
          required
          fullWidth
          sx={{ mb: 1 }}
        />

        <FormInput
          label="Password"
          required
          fullWidth
          name="password"
          type="password"
          autoComplete="current-password"
          sx={{ mb: 1 }}
        />

        <Grid container alignItems="center">
          <Grid item xs>
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            />
          </Grid>

          <Grid item>
            <Typography
              variant="body1"
              color="grey.600"
              onClick={onClickForgot}
              sx={{ cursor: 'pointer' }}
            >
              Forgot password?
            </Typography>
          </Grid>
        </Grid>

        <Grid container justifyContent="center" mt={2}>
          <Grid item xs={12} sm={6}>
            <LoadingButton
              loading={authLoading}
              disabled={!isDirty || !isValid}
              fullWidth
              variant="contained"
              type="submit"
            >
              Sign In
            </LoadingButton>

            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mt={2}>
              <Typography variant="body1" color="grey.600">
                Don&apos;t have an account?
              </Typography>

              <Link to={routes.signUp}>
                <Typography variant="body1" color="info.main">
                  Sign Up
                </Typography>
              </Link>
            </Stack>
          </Grid>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mt={2}>
            <Typography variant="body1" color="grey.600">
              Have you changed your organization?
            </Typography>

            <Link to={routes.changeOrganization}>
              <Typography variant="body1" color="info.main">
                Add Institutional Access code.
              </Typography>
            </Link>
          </Stack>
        </Grid>
      </Box>
    </FormProvider>
  )
}
